/**
 * Resolves the signer parties + PDF paths for the Contract, Insurance,
 * and e-Invoice popups based on the active scenario + audience perspective.
 *
 * The popup CTA reflects the role of the party acting from that
 * perspective (Sign / Accept / Acknowledge). The PDF is per-scenario only —
 * T1 + T2 share the poultry-trade docs, T3 uses the urea-fertilizer variants.
 */

export type SignerBlock = {
  signedAs:    string;
  signerOrg:   string;
  actionLabel: string;
};

export type SignParty = {
  contract:     SignerBlock;
  einvoice:     SignerBlock;
  insurance:    SignerBlock;
  contractPdf:  string;
  einvoicePdf:  string;
  insurancePdf: string;
};

export type SignPerspective = "exporter" | "importer";

const AURORA_PDFS = {
  contractPdf:  "/sign-docs/contract.pdf",
  einvoicePdf:  "/sign-docs/einvoice.pdf",
  insurancePdf: "/sign-docs/insurance.pdf",
} as const;

const GULF_PDFS = {
  contractPdf:  "/sign-docs/contract-t3.pdf",
  einvoicePdf:  "/sign-docs/einvoice-t3.pdf",
  insurancePdf: "/sign-docs/insurance-t3.pdf",
} as const;

const VERDE_PDFS = {
  contractPdf:  "/sign-docs/contract-t4.pdf",
  einvoicePdf:  "/sign-docs/einvoice-t4.pdf",
  insurancePdf: "/sign-docs/insurance-t4.pdf",
} as const;

// T1 + T2 — Aurora (BR) seller, Al Madar (UAE) buyer, CIF (seller arranges insurance).
const AURORA_AL_MADAR: Record<SignPerspective, SignParty> = {
  exporter: {
    contract: {
      signedAs:    "Aurora Aves Brasil S.A.",
      signerOrg:   "Seller (Exporter) · BR",
      actionLabel: "Sign as Aurora Aves Brasil S.A.",
    },
    einvoice: {
      signedAs:    "Aurora Aves Brasil S.A.",
      signerOrg:   "Seller (Exporter · Issuer) · BR",
      actionLabel: "Sign as Aurora Aves Brasil S.A.",
    },
    insurance: {
      signedAs:    "Aurora Aves Brasil S.A.",
      signerOrg:   "Insured (Seller arranges under CIF) · BR",
      actionLabel: "Sign as Aurora Aves Brasil S.A.",
    },
    ...AURORA_PDFS,
  },
  importer: {
    contract: {
      signedAs:    "Al Madar Foods LLC",
      signerOrg:   "Buyer (Importer) · UAE",
      actionLabel: "Sign as Al Madar Foods LLC",
    },
    einvoice: {
      signedAs:    "Al Madar Foods LLC",
      signerOrg:   "Buyer (Importer · Acceptor) · UAE",
      actionLabel: "Accept as Al Madar Foods LLC",
    },
    insurance: {
      signedAs:    "Al Madar Foods LLC",
      signerOrg:   "Beneficiary at destination · UAE",
      actionLabel: "Acknowledge as Al Madar Foods LLC",
    },
    ...AURORA_PDFS,
  },
};

// T4 air-cargo — Verde Carnes (BR) seller, Al Bayt (UAE) buyer, CIP
// (seller arranges carriage + insurance to destination).
const VERDE_ALBAYT: Record<SignPerspective, SignParty> = {
  exporter: {
    contract: {
      signedAs:    "Verde Carnes Brasil S.A.",
      signerOrg:   "Seller (Exporter) · BR",
      actionLabel: "Sign as Verde Carnes Brasil S.A.",
    },
    einvoice: {
      signedAs:    "Verde Carnes Brasil S.A.",
      signerOrg:   "Seller (Exporter · Issuer) · BR",
      actionLabel: "Sign as Verde Carnes Brasil S.A.",
    },
    insurance: {
      signedAs:    "Verde Carnes Brasil S.A.",
      signerOrg:   "Insured (Seller arranges under CIP) · BR",
      actionLabel: "Sign as Verde Carnes Brasil S.A.",
    },
    ...VERDE_PDFS,
  },
  importer: {
    contract: {
      signedAs:    "Al Bayt Premium Meats LLC",
      signerOrg:   "Buyer (Importer) · UAE",
      actionLabel: "Sign as Al Bayt Premium Meats LLC",
    },
    einvoice: {
      signedAs:    "Al Bayt Premium Meats LLC",
      signerOrg:   "Buyer (Importer · Acceptor) · UAE",
      actionLabel: "Accept as Al Bayt Premium Meats LLC",
    },
    insurance: {
      signedAs:    "Al Bayt Premium Meats LLC",
      signerOrg:   "Beneficiary at destination · UAE",
      actionLabel: "Acknowledge as Al Bayt Premium Meats LLC",
    },
    ...VERDE_PDFS,
  },
};

// T3 reverse — Gulf Phosphate (UAE) seller, Pampagro (BR) buyer, FOB
// (buyer arranges insurance).
const GULF_PAMPAGRO: Record<SignPerspective, SignParty> = {
  exporter: {
    contract: {
      signedAs:    "Gulf Phosphate UAE FZE",
      signerOrg:   "Seller (Exporter) · UAE",
      actionLabel: "Sign as Gulf Phosphate UAE FZE",
    },
    einvoice: {
      signedAs:    "Gulf Phosphate UAE FZE",
      signerOrg:   "Seller (Exporter · Issuer) · UAE",
      actionLabel: "Sign as Gulf Phosphate UAE FZE",
    },
    insurance: {
      signedAs:    "Gulf Phosphate UAE FZE",
      signerOrg:   "Beneficiary at origin · UAE",
      actionLabel: "Acknowledge as Gulf Phosphate UAE FZE",
    },
    ...GULF_PDFS,
  },
  importer: {
    contract: {
      signedAs:    "Pampagro Importação Ltda",
      signerOrg:   "Buyer (Importer) · BR",
      actionLabel: "Sign as Pampagro Importação Ltda",
    },
    einvoice: {
      signedAs:    "Pampagro Importação Ltda",
      signerOrg:   "Buyer (Importer · Acceptor) · BR",
      actionLabel: "Accept as Pampagro Importação Ltda",
    },
    insurance: {
      signedAs:    "Pampagro Importação Ltda",
      signerOrg:   "Insured (Buyer arranges under FOB) · BR",
      actionLabel: "Sign as Pampagro Importação Ltda",
    },
    ...GULF_PDFS,
  },
};

/**
 * Map an SSE-derived perspective string ("E", "E-GP", "I-AM", "I-SV", "split", ...)
 * down to the binary "exporter" / "importer" axis the popup signer keys on.
 * Defaults to "exporter" if unknown or split.
 */
export function normalisePerspective(perspective: string | null | undefined): SignPerspective {
  if (!perspective) return "exporter";
  if (perspective.startsWith("I-") || perspective === "I") return "importer";
  return "exporter";
}

export function resolveSignParty(scenarioId: string, perspective: SignPerspective): SignParty {
  if (scenarioId === "t3-reverse-corridor") return GULF_PAMPAGRO[perspective];
  if (scenarioId === "t4-air-cargo")        return VERDE_ALBAYT[perspective];
  return AURORA_AL_MADAR[perspective];
}
